/**
 * Single source of truth for user-facing anomaly explanation text.
 *
 * The anomaly service hands us verified numbers (anomaly_evidence_v1, built from
 * the frozen feature table under an identity check). This module turns them into
 * Russian sentences and NOTHING ELSE: it never counts, averages, extrapolates or
 * back-fills a missing figure. A reason the service could not support simply has
 * no entry in `reasons`, and then we stay silent about it rather than falling
 * back on the generic dictionary template — a template with no numbers reads as
 * a finding, which is exactly what it must not do.
 *
 * SHAPE OF AN EXPLANATION
 * Each reason renders as: a title that states the conclusion, one short sentence
 * carrying the evidence, and secondary `meta` lines for the things a reader only
 * needs on demand (comparison period, cargo code, side counts). Numbers and
 * periods come from the evidence; the wagon/container number and cargo codes
 * come from the case row itself — the same authoritative fields the drawer shows
 * under «Детали кейса», never parsed back out of prose.
 *
 * LANGUAGE
 * Written for a reader who knows the freight domain but not this model. No
 * "переход груза", "юнит", "единица", "когорта", "корпус", no feature-column or
 * reason-code names — those belong to «Технические сведения».
 */

import { getVisibleAnomalyReasons } from './anomaly-reason-visibility'
import {
  pushEvidence,
  MIN_EVIDENCE_ROWS,
  MAX_EVIDENCE_EXAMPLES,
  type EvidenceRow,
  type EvidenceDrilldown,
} from './evidence-row'
import type {
  AnomalyCaseRow,
  AnomalyExplanation,
  AnomalyReasonEvidence,
} from './anomaly-cases-v3.types'
import {
  BURST_EXAMPLES_NOTE,
  BURST_POPULATION_TOOLTIP,
  EXPORT_CARGO_VARIANT_TOOLTIP,
  IMPORT_CARGO_EXAMPLES_NOTE,
  STATION_CORRIDOR_TOOLTIP,
  IMPORT_CARGO_POPULATION_TOOLTIP,
  PRIOR_YEARS_TOOLTIP,
  TRANSITION_KEY_TOOLTIP,
  TRANSITION_VARIANT_TOOLTIP,
  importCargoCodesLabel,
  importCargoKeyLabel,
} from './anomaly-semantics'

import type { Formatter } from '@/lib/i18n'

/** Shown once, near the end of the drawer — never repeated per section. */
export const ANOMALY_DISCLAIMER =
  'Оценки помогают выбрать кейсы для проверки и не подтверждают нарушение.'

export const NO_EVIDENCE_TEXT =
  'Для этого кейса в текущем снимке не сохранены числовые данные, ' +
  'необходимые для точного объяснения причины.'

/**
 * Почему под баллом нет числовых оснований.
 *
 * ЗАЧЕМ РАЗЛИЧАТЬ. Прежде любое отсутствие оснований объяснялось одной фразой —
 * «в текущем снимке не сохранены числовые данные». Для большинства кейсов это
 * прямая неправда: данные сохранены, а числового ОСНОВАНИЯ не существует.
 * Словарь причин аномалии целиком историчен (`NEW_UNIT_*`, `UNSEEN_*`,
 * `*_VS_UNIT_HISTORY`), и у перевозки первого года корпуса сравнивать не с чем:
 * в сохранённой строке признаков все шесть флагов равны `false`, а не пусты.
 *
 * Проверено на текущем снимке (29 689 кейсов): 4 018 кейсов когорты
 * `same_unit_no_history_wagon` — все 2022 года — не имеют ни одного кода
 * причины; ещё 3 365 кейсов когорты с историей кодов тоже не получили; и лишь
 * 234 кейса имеют коды, но не имеют чисел. Одна формулировка на три разных
 * факта вводила аналитика в заблуждение в 7 383 случаях из 7 617.
 */
export type NoEvidenceCause =
  /** Модель назвала причины, но проверенных чисел под ними нет. */
  | 'reasons_without_numbers'
  /** Сравнивать не с чем: у единицы нет прошлой истории (первый год корпуса). */
  | 'no_history_baseline'
  /** История есть, но ни один числовой признак не сработал. */
  | 'no_reasons_flagged'

/**
 * Причина пустого раздела — из полей, которые API уже отдаёт.
 *
 * Ничего не выводится из балла, похожих кейсов или SHAP: они относятся к другим
 * моделям и другим популяциям.
 */
export function noEvidenceCause(
  explanation: AnomalyExplanation | undefined,
  cohort?: string | null,
): NoEvidenceCause {
  if ((explanation?.unsupportedReasonCodes?.length ?? 0) > 0) return 'reasons_without_numbers'
  return typeof cohort === 'string' && cohort.includes('no_history')
    ? 'no_history_baseline'
    : 'no_reasons_flagged'
}

/** Текст под каждое состояние. Ни один не обещает того, чего в снимке нет. */
export function noEvidenceText(cause: NoEvidenceCause, ctx: ExplanationContext = DEFAULT_CTX): string {
  switch (cause) {
    case 'reasons_without_numbers':
      return 'Модель выделила конкретную причину, но для неё в текущей версии ' +
        'объяснений нет подтверждённой числовой детализации.'
    case 'no_history_baseline': {
      // Утверждается ровно то, что доказано: исторических связок нет В ДОСТУПНОЙ
      // ИСТОРИИ. Про реальный мир — что вагон до 2022 года не ездил — не сказано
      // ничего, потому что данные этого не показывают.
      const earliest = ctx.caseYear === EARLIEST_HISTORY_YEAR
        ? `Кейс относится к ${ctx.caseYear} году — это самый ранний период, доступный ` +
          'системе для сравнения. '
        : ''
      return 'Недостаточно предыдущей истории для детального сравнения. ' + earliest +
        `До этого кейса в доступной истории нет исторических связок «импорт → экспорт» ` +
        `${UNIT_GEN[ctx.unitKind]}, с которыми можно было бы сравнить его поведение. Поэтому общий уровень ` +
        'необычности рассчитан, но отдельные исторические причины и их числовые ' +
        'значения для этого кейса не сформированы.'
    }
    case 'no_reasons_flagged':
      return 'Модель оценила кейс как необычный по сочетанию характеристик перевозки, ' +
        'но отдельную доминирующую причину не выделила.'
  }
}

// Раньше здесь жила формулировка «сочетание импортной и экспортной отправок». Она
// описывала показатель шире, чем он есть: сравнивается ТОЛЬКО пара кодов груза
// (`canonical_transition_key` = код груза импортной ноги → код груза экспортной ноги),
// а станции, страны, участники и номер вагона в сравнение не входят. Поэтому во всех
// текстах теперь стоит «комбинация кодов груза», а её точный состав — в подсказке
// `TRANSITION_KEY_TOOLTIP`.

/** Display priority — strongest, most concrete statements first. */
const REASON_PRIORITY: Record<string, number> = {
  UNSEEN_GLOBAL_CARGO_TRANSITION: 100,
  NEW_UNIT_TRANSITION: 95,
  TRANSITION_ACTIVITY_BURST: 90,
  UNSEEN_CARGO_STATION_ROUTE: 85,
  NEW_UNIT_EXPORT_CARGO: 80,
  HIGH_GLOBAL_TRANSITION_SURPRISAL: 75,
  HIGH_CARGO_ROUTE_SURPRISAL: 70,
  UNSEEN_PARTICIPANT_CHAIN: 65,
  UNSEEN_PAYER_TRANSITION: 60,
  UNUSUAL_INTERVAL_VS_UNIT_HISTORY: 55,
  UNUSUAL_WEIGHT_VS_UNIT_HISTORY: 50,
  UNUSUAL_ROUTE_INTERVAL: 45,
}

const MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
]

function isNum(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v)
}

/** ru-RU thousands separators; integers stay integers. */
export function num(n: number, f: Formatter): string {
  return f.number(n, { maximumFractionDigits: 2 })
}

/** Russian plural: plural(2, 'раз', 'раза', 'раз') → 'раза'. */
export function plural(n: number, one: string, few: string, many: string): string {
  const a = Math.abs(Math.trunc(n))
  const mod10 = a % 10
  const mod100 = a % 100
  if (mod10 === 1 && mod100 !== 11) return one
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few
  return many
}

export function count(n: number, one: string, few: string, many: string, f: Formatter): string {
  return `${num(n, f)} ${plural(n, one, few, many)}`
}

/**
 * Counted form after a verb: «найдена 1 историческая связка», «найдено 2 исторические
 * связки», «найдено 39 исторических связок».
 *
 * ЕДИНИЦА СЧЁТА — СВЯЗКА, А НЕ ПЕРЕВОЗКА. За каждым из этих чисел стоит строка корпуса
 * сопоставленных пар: одна импортная запись и одна экспортная запись, которые
 * историческая логика сопоставления соединила в пару «импорт → экспорт». Это фон для
 * сравнения, а не подтверждённая физическая перевозка и не кейс реестра, поэтому слово
 * «перевозка» здесь неверно — см.
 * ai_transit/v2/reports/ANOMALY_EVIDENCE_TERMINOLOGY_AUDIT.md.
 *
 * СЛОВО «СВЯЗКА» ВСЕГДА УТОЧНЯЕТСЯ ТЕМ, ЧТО СВЯЗАНО: «связка «импорт → экспорт»» —
 * строка корпуса; «связка участников», «связка плательщиков» — пары ключей внутри
 * одного кейса. Голое «связка» стоит только там, где уточнение уже прозвучало рядом.
 */
function links(n: number, f: Formatter): string {
  return count(n, 'историческая связка', 'исторические связки', 'исторических связок', f)
}

/**
 * Genitive form, required after «среди …». Russian case government wins over the
 * count here: «среди 644 предыдущих перевозок», not «…предыдущие перевозки».
 */
function tripsGen(n: number, f: Formatter): string {
  const a = Math.abs(Math.trunc(n))
  const one = a % 10 === 1 && a % 100 !== 11
  return `${num(n, f)} ${one ? 'предыдущей перевозки' : 'предыдущих перевозок'}`
}

/** Past-tense agreement for a counted subject: «была 1 …» vs «было 39 …». */
function wasWere(n: number): string {
  const a = Math.abs(Math.trunc(n))
  return a % 10 === 1 && a % 100 !== 11 ? 'была' : 'было'
}

/** «16 марта 2026 года» — used when a date sits inside a sentence. */
export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return null
  const [, y, mo, d] = m
  const mi = Number(mo) - 1
  if (mi < 0 || mi > 11) return null
  return `${Number(d)} ${MONTHS_GEN[mi]} ${Number(y)} года`
}

/** «16 марта 2026» — compact form for the secondary period line. */
export function formatDateShort(iso: string | null | undefined): string | null {
  const full = formatDate(iso)
  return full ? full.replace(/ года$/, '') : null
}

/**
 * «Период сравнения: 1 января 2022 — 31 декабря 2025», or null when either
 * bound is missing — an unknown period is simply not shown.
 */
function periodLine(from: string | null, to: string | null, label = 'Период сравнения'): string | null {
  const a = formatDateShort(from)
  const b = formatDateShort(to)
  return a && b ? `${label}: ${a} — ${b}` : null
}

/** «в 21 раз» / «в 1,5 раза» — only for a multiplier the service supplied. */
function timesPhrase(mult: number): string {
  const rounded = Math.round(mult * 10) / 10
  const isInt = Number.isInteger(rounded)
  const shown = isInt ? String(rounded) : rounded.toFixed(1).replace('.', ',')
  return `в ${shown} ${isInt ? plural(rounded, 'раз', 'раза', 'раз') : 'раза'}`
}

// ── case context: identity shown in prose, taken from the row ────────────────

export interface ExplanationContext {
  unitKind: 'wagon' | 'container'
  /** «52866548», or null when the row carries no identifier. */
  unitNumber: string | null
  /**
   * «ЕТСНГ 324116 / ГНГ 72107010» — ОБА кода импортной ноги, как в «Деталях кейса»
   * (`cargoEtsng` = `import_etsng`, `cargoGng` = `import_gng`).
   */
  cargoCode: string | null
  /**
   * Тот единственный код, который реально попадает в ключ отбора популяции:
   * «ГНГ 86050000», а при отсутствии ГНГ — «ЕТСНГ 421034».
   */
  importCargoKey: string | null
  /** Когорта сравнения аномалии — по ней различается причина пустого раздела. */
  anomalyCohort?: string | null
  /**
   * Год самой перевозки — берётся из её же дат, ничего не досчитывается.
   * Нужен ровно для одного: сказать аналитику простыми словами, что кейс
   * стоит в самом раннем году, доступном для сравнения.
   */
  caseYear?: number | null
}

export function explanationContext(row: AnomalyCaseRow): ExplanationContext {
  const unitKind: 'wagon' | 'container' =
    row.physicalUnitType === 'container' || (!row.wagon && !!row.container) ? 'container' : 'wagon'
  return {
    unitKind,
    unitNumber: (unitKind === 'container' ? row.container : row.wagon) || null,
    cargoCode: importCargoCodesLabel(row),
    importCargoKey: importCargoKeyLabel(row),
    anomalyCohort: row.anomalyCohort ?? null,
    caseYear: yearOf(row.importDate) ?? yearOf(row.exportDate),
  }
}

/**
 * Самый ранний год, до которого исторические правила не видят ничего.
 *
 * Это не догадка: сборщик доказательств отсекает предысторию по
 * `PRIOR_HISTORY_FLOOR_YEAR = 2022` (`build_anomaly_evidence_v1.py`), поэтому
 * для перевозки 2022 года предыдущих перевозок в доступной истории нет по
 * построению. Число вынесено сюда, чтобы фраза для аналитика не расходилась с
 * тем, как на самом деле считаются признаки.
 */
const EARLIEST_HISTORY_YEAR = 2022

/** «2022-02-09» → 2022. Ничего не парсит как дату — берёт первые четыре цифры. */
function yearOf(date: string | null | undefined): number | null {
  const m = typeof date === 'string' ? /^(\d{4})/.exec(date) : null
  return m ? Number(m[1]) : null
}

const UNIT_GEN = { wagon: 'вагона', container: 'контейнера' } as const
const UNIT_NOM = { wagon: 'вагона', container: 'контейнера' } as const

/** «вагона 52866548» — falls back to «вагона» when the number is unknown. */
function unitGenWithNumber(ctx: ExplanationContext): string {
  const base = UNIT_GEN[ctx.unitKind]
  return ctx.unitNumber ? `${base} ${ctx.unitNumber}` : base
}

const DEFAULT_CTX: ExplanationContext = {
  unitKind: 'wagon', unitNumber: null, cargoCode: null, importCargoKey: null,
  anomalyCohort: null, caseYear: null,
}

/** « (ГНГ 86050000)» — добавляется к описанию отбора только когда код известен. */
function importKeySuffix(ctx: ExplanationContext): string {
  return ctx.importCargoKey ? ` (${ctx.importCargoKey})` : ''
}

// ── drill-down: which numbers have a set of rows behind them ──────────────────
//
// Only counts over a concrete historical set get one. An average, a multiplier, a
// percentile, this case's own interval or weight difference and the comparison period
// are NOT sets of rows — they carry a tooltip instead, and the table renders no button
// for them. The rule lives here, next to the sentences, so a new reason cannot quietly
// acquire a list it has no set for.

const EVIDENCE_EXAMPLES_ENDPOINT =
  '/api/ai/v1/analytics/anomalies?view=case-evidence-examples'

/**
 * `REASON_CODE.dataset_type` — the same key the service stores next to the number, so
 * the count and the rows are provably about one and the same measure.
 *
 * Returns null when the count is missing: a number we do not have cannot be enumerated.
 */
function drilldownFor(
  code: string,
  datasetType: string,
  total: unknown,
  title: string,
  selection: string,
  columnsNote?: string,
): EvidenceDrilldown | null {
  if (!isNum(total) || total < 0) return null
  return {
    evidenceKey: `${code}.${datasetType}`,
    datasetType,
    totalCount: Math.trunc(total),
    maxDisplayedItems: MAX_EVIDENCE_EXAMPLES,
    title,
    endpoint: EVIDENCE_EXAMPLES_ENDPOINT,
    selectionDescription: selection,
    columnsNote: columnsNote ?? null,
  }
}

const UNIT_PRIOR_SELECTION =
  'все сопоставленные пары «импорт → экспорт» этой физической единицы, ' +
  'зафиксированные до текущего кейса, от новых к старым'

// ── rendering ────────────────────────────────────────────────────────────────

export interface RenderedReason {
  code: string
  /** Conclusion, stated up front. */
  title: string
  /**
   * ONE short sentence stating what happened. Stage 39: it no longer repeats the numbers —
   * they are in `evidence` below it, where they can be read at a glance.
   */
  text: string
  /** The numbers, as a two-column table. Empty when the snapshot cannot support one. */
  evidence: EvidenceRow[]
  /** One line under the table — window semantics only, never a second finding. */
  note?: string | null
  /**
   * Checklist line for «Что проверить аналитику» — the same numbers in imperative mood,
   * never the whole table. Null when the reason has no supported numbers to point at.
   */
  checklistText?: string | null
}

/** Shown in place of the table when a reason has fewer than two supported numbers. */
export const INSUFFICIENT_EVIDENCE_TEXT =
  'В текущем снимке недостаточно числовых данных для точного объяснения этой причины.'

/** «01.01.2022 — 31.12.2025», or null when either bound is missing. */
function periodValue(from: string | null, to: string | null): string | null {
  const a = formatDateShort(from)
  const b = formatDateShort(to)
  return a && b ? `${a} — ${b}` : null
}

/**
 * Render one evidence record. Returns null when the numbers needed for an honest
 * sentence are absent — the caller then drops the reason entirely.
 */
export function renderReason(
  f: Formatter,
  e: AnomalyReasonEvidence,
  ctx: ExplanationContext = DEFAULT_CTX,
): RenderedReason | null {
  const period = periodValue(e.baselinePeriodStart, e.baselinePeriodEnd)
  const unitGen = unitGenWithNumber(ctx)
  const unitNom = UNIT_NOM[ctx.unitKind]
  const rows: EvidenceRow[] = []

  switch (e.code) {
    // Популяция отобрана ТОЛЬКО по коду груза импортной ноги (canonical_import_cargo_key),
    // а «сочетание» — это пара кодов «ввоз → вывоз». Подпись обязана называть и то, и
    // другое: общий текст «с этим кодом груза» обещал совпадение обоих кодов, которого в
    // отборе нет.
    case 'UNSEEN_GLOBAL_CARGO_TRANSITION': {
      if (!isNum(e.corpusSize) || e.corpusSize <= 0) return null
      pushEvidence(rows, 'Связок с таким же кодом груза на ввоз', num(e.corpusSize, f), {
        tooltip: IMPORT_CARGO_POPULATION_TOOLTIP,
        drilldown: drilldownFor(e.code, 'canonical_import_cargo_prior_pairs', e.corpusSize,
          'Связки «импорт → экспорт» с таким же кодом груза на ввоз',
          `сопоставленные пары «импорт → экспорт», у которых код груза импортной ноги совпадает с текущим${importKeySuffix(ctx)}, до текущего кейса — от новых к старым`,
          IMPORT_CARGO_EXAMPLES_NOTE),
      })
      pushEvidence(rows, 'Совпадений с текущей комбинацией кодов', '0', {
        tooltip: TRANSITION_KEY_TOOLTIP,
      })
      pushEvidence(rows, 'Код отбора (груз на ввоз)', ctx.importCargoKey, {
        tooltip: IMPORT_CARGO_POPULATION_TOOLTIP,
      })
      pushEvidence(rows, 'Период сравнения', period)
      pushEvidence(rows, 'Код груза', ctx.cargoCode)
      return {
        code: e.code,
        title: 'Новая комбинация кодов груза в доступных данных',
        text: 'Среди исторических связок «импорт → экспорт» с таким же кодом груза на ввоз ' +
          'текущая комбинация «код груза на ввоз → код груза на вывоз» ранее не встречалась.',
        evidence: rows,
        checklistText: `Проверить документальное основание новой комбинации кодов груза (сравнение по ${num(e.corpusSize, f)} историческим связкам с таким же кодом груза на ввоз)`,
      }
    }

    // «Первый случай для вагона» читался как противоречие рядом с «предыдущих перевозок —
    // 18»: первой является не перевозка вагона, а комбинация кодов груза. Заголовок и
    // текст теперь называют обе величины сразу, а состав комбинации стоит в подсказке.
    case 'NEW_UNIT_TRANSITION': {
      if (!isNum(e.historyCount)) return null
      pushEvidence(rows, `Исторических связок для ${UNIT_GEN[ctx.unitKind]}`, num(e.historyCount, f), {
        tooltip: PRIOR_YEARS_TOOLTIP,
        drilldown: drilldownFor(e.code, 'unit_prior_pairs', e.historyCount,
          `Исторические связки «импорт → экспорт» для ${UNIT_GEN[ctx.unitKind]}`, UNIT_PRIOR_SELECTION),
      })
      pushEvidence(rows, 'Совпадений с текущей комбинацией', '0', {
        tooltip: TRANSITION_KEY_TOOLTIP,
      })
      // `corpusSize` here is the number of OTHER cargo-movement variants the unit showed in
      // the same period — shown only when it is a real, positive count.
      if (isNum(e.corpusSize) && e.corpusSize > 0) {
        pushEvidence(rows, 'Вариантов комбинации кодов груза', num(e.corpusSize, f), {
          tooltip: TRANSITION_VARIANT_TOOLTIP,
          drilldown: drilldownFor(e.code, 'unit_prior_transition_variants', e.corpusSize,
            `Варианты комбинации кодов груза для ${UNIT_GEN[ctx.unitKind]}`,
            'группы исторических связок этой физической единицы по паре кодов груза «ввоз → вывоз», от частых к редким'),
        })
      }
      pushEvidence(rows, 'Период сравнения', period)
      const variantsSentence = isNum(e.corpusSize) && e.corpusSize > 0
        ? ` Они образуют ${count(e.corpusSize, 'вариант', 'варианта', 'вариантов', f)} по комбинации кодов груза.`
        : ''
      return {
        code: e.code,
        title: `Новая комбинация для ${UNIT_GEN[ctx.unitKind]}`,
        text: `Для ${unitGen} найдено ${links(e.historyCount, f)} «импорт → экспорт».` + variantsSentence +
          ' Текущая комбинация кодов груза среди них ранее не встречалась.',
        evidence: rows,
        checklistText: `Проверить новую комбинацию кодов груза для ${unitGen}: ${num(e.historyCount, f)} исторических связок, совпадений — 0`,
      }
    }

    case 'NEW_UNIT_EXPORT_CARGO': {
      if (!isNum(e.historyCount)) return null
      pushEvidence(rows, `Исторических связок для ${UNIT_GEN[ctx.unitKind]}`, num(e.historyCount, f), {
        tooltip: PRIOR_YEARS_TOOLTIP,
        drilldown: drilldownFor(e.code, 'unit_prior_pairs', e.historyCount,
          `Исторические связки «импорт → экспорт» для ${UNIT_GEN[ctx.unitKind]}`, UNIT_PRIOR_SELECTION),
      })
      pushEvidence(rows, 'Совпадений по коду груза на вывоз', '0', {
        tooltip: EXPORT_CARGO_VARIANT_TOOLTIP,
      })
      if (isNum(e.corpusSize) && e.corpusSize > 0) {
        pushEvidence(rows, 'Исторических вариантов груза на вывоз', num(e.corpusSize, f), {
          tooltip: EXPORT_CARGO_VARIANT_TOOLTIP,
          drilldown: drilldownFor(e.code, 'unit_prior_export_cargo_variants', e.corpusSize,
            'Исторические варианты груза на вывоз',
            'группы исторических связок этой физической единицы по коду груза экспортной ноги, от частых к редким'),
        })
      }
      pushEvidence(rows, 'Период сравнения', period)
      pushEvidence(rows, 'Код груза', ctx.cargoCode)
      return {
        code: e.code,
        title: `Новый код груза на вывоз для ${UNIT_GEN[ctx.unitKind]}`,
        text: `Для ${unitGen} найдено ${links(e.historyCount, f)} «импорт → экспорт». ` +
          'Ни в одной из них груз на вывоз не имел текущего кода.',
        evidence: rows,
        checklistText: `Проверить, почему для ${unitGen} впервые вывозится груз с этим кодом (${num(e.historyCount, f)} исторических связок, совпадений — 0)`,
      }
    }

    case 'UNSEEN_CARGO_STATION_ROUTE': {
      if (!isNum(e.corpusSize) || e.corpusSize <= 0) return null
      pushEvidence(rows, 'Связок по этому коридору станций', num(e.corpusSize, f), {
        tooltip: STATION_CORRIDOR_TOOLTIP,
        drilldown: drilldownFor(e.code, 'station_corridor_prior_pairs', e.corpusSize,
          'Связки «импорт → экспорт» по этому коридору станций',
          'сопоставленные пары «импорт → экспорт» по этому коридору станций до текущего кейса, от новых к старым'),
      })
      pushEvidence(rows, 'Совпадений по коду груза', '0', { tooltip: TRANSITION_KEY_TOOLTIP })
      pushEvidence(rows, 'Период сравнения', period)
      pushEvidence(rows, 'Код груза', ctx.cargoCode)
      return {
        code: e.code,
        title: 'Первый случай этого груза на таком коридоре станций',
        text: 'По этому коридору станций такая комбинация кодов груза среди исторических ' +
          'связок «импорт → экспорт» ранее не встречалась.',
        evidence: rows,
        checklistText: `Проверить перевозку этого груза по ранее не использовавшемуся маршруту (сравнение по ${num(e.corpusSize, f)} историческим связкам)`,
      }
    }

    // The two "rare" reasons can legitimately carry an observed count of 0 — the model
    // ranks by rarity, and the rarest value is none at all.
    case 'HIGH_GLOBAL_TRANSITION_SURPRISAL': {
      if (!isNum(e.observedValue) || !isNum(e.corpusSize) || e.corpusSize <= 0) return null
      const seen = e.observedValue > 0
      pushEvidence(rows, 'Связок с таким же кодом груза на ввоз', num(e.corpusSize, f), {
        tooltip: IMPORT_CARGO_POPULATION_TOOLTIP,
        drilldown: drilldownFor(e.code, 'canonical_import_cargo_prior_pairs', e.corpusSize,
          'Связки «импорт → экспорт» с таким же кодом груза на ввоз',
          `сопоставленные пары «импорт → экспорт», у которых код груза импортной ноги совпадает с текущим${importKeySuffix(ctx)}, до текущего кейса — от новых к старым`,
          IMPORT_CARGO_EXAMPLES_NOTE),
      })
      pushEvidence(rows, 'Совпадений с текущей комбинацией кодов', num(e.observedValue, f), {
        tooltip: TRANSITION_KEY_TOOLTIP,
        drilldown: drilldownFor(e.code, 'canonical_transition_prior_pairs', e.observedValue,
          'Связки с такой же комбинацией кодов груза',
          'сопоставленные пары «импорт → экспорт» с той же парой кодов груза, зафиксированные до текущего кейса'),
      })
      pushEvidence(rows, 'Код отбора (груз на ввоз)', ctx.importCargoKey, {
        tooltip: IMPORT_CARGO_POPULATION_TOOLTIP,
      })
      pushEvidence(rows, 'Период сравнения', period)
      pushEvidence(rows, 'Код груза', ctx.cargoCode)
      return {
        code: e.code,
        title: seen ? 'Редкая комбинация кодов груза' : 'Новая комбинация кодов груза в доступных данных',
        text: seen
          ? 'Среди исторических связок «импорт → экспорт» с таким же кодом груза на ввоз ' +
            'текущая комбинация «код груза на ввоз → код груза на вывоз» встречалась редко.'
          : 'Среди исторических связок «импорт → экспорт» с таким же кодом груза на ввоз ' +
            'текущая комбинация «код груза на ввоз → код груза на вывоз» ранее не встречалась.',
        evidence: rows,
        checklistText: `Проверить редкую комбинацию кодов груза: ${num(e.observedValue, f)} случаев на ${num(e.corpusSize, f)} исторических связок с таким же кодом груза на ввоз`,
      }
    }

    case 'HIGH_CARGO_ROUTE_SURPRISAL': {
      if (!isNum(e.observedValue) || !isNum(e.corpusSize) || e.corpusSize <= 0) return null
      const seen = e.observedValue > 0
      pushEvidence(rows, 'Связок по этому коридору станций', num(e.corpusSize, f), {
        tooltip: STATION_CORRIDOR_TOOLTIP,
        drilldown: drilldownFor(e.code, 'station_corridor_prior_pairs', e.corpusSize,
          'Связки «импорт → экспорт» по этому коридору станций',
          'сопоставленные пары «импорт → экспорт» по этому коридору станций до текущего кейса, от новых к старым'),
      })
      pushEvidence(rows, 'Совпадений по коду груза', num(e.observedValue, f), {
        tooltip: TRANSITION_KEY_TOOLTIP,
        drilldown: drilldownFor(e.code, 'cargo_station_corridor_prior_pairs', e.observedValue,
          'Связки с такой же комбинацией кодов груза по этому коридору',
          'сопоставленные пары «импорт → экспорт» с той же парой кодов груза по этому коридору станций, зафиксированные до текущего кейса'),
      })
      pushEvidence(rows, 'Период сравнения', period)
      pushEvidence(rows, 'Код груза', ctx.cargoCode)
      return {
        code: e.code,
        title: seen ? 'Редкий коридор станций для этого груза' : 'Первый случай этого груза на таком коридоре станций',
        text: seen
          ? 'По этому коридору станций такая комбинация кодов груза встречалась редко.'
          : 'По этому коридору станций такая комбинация кодов груза ранее не встречалась.',
        evidence: rows,
        checklistText: `Проверить редкий коридор станций для этого груза: ${num(e.observedValue, f)} случаев на ${num(e.corpusSize, f)} исторических связок по коридору`,
      }
    }

    case 'UNSEEN_PARTICIPANT_CHAIN':
      pushEvidence(rows, 'Таких связок участников ранее', '0')
      pushEvidence(rows, 'Период сравнения', period)
      return {
        code: e.code,
        title: 'Новая связка отправителя и получателя',
        text: 'Такая связка участников импорта и экспорта раньше не встречалась.',
        evidence: rows,
      }

    case 'UNSEEN_PAYER_TRANSITION':
      pushEvidence(rows, 'Таких пар плательщиков ранее', '0')
      pushEvidence(rows, 'Период сравнения', period)
      return {
        code: e.code,
        title: 'Новая связка плательщиков',
        text: 'Такая пара плательщиков импорта и экспорта раньше не встречалась.',
        evidence: rows,
      }

    case 'TRANSITION_ACTIVITY_BURST': {
      // The service only emits this with observed >= 1 and baseline >= 1, so the mean
      // below can never divide by zero and a burst is never claimed on an empty window.
      if (!isNum(e.observedValue) || !isNum(e.baselineValue) || e.baselineValue <= 0) return null
      // The 90-day window CONTAINS the 30-day one (anomaly_evidence_v1 windowSemantics),
      // so the honest comparison is against the mean 30 days inside it — not against a
      // separate «previous 90 days», which does not exist.
      const mean30 = e.baselineValue / 3
      const meanText = Number.isInteger(mean30) ? num(mean30, f) : mean30.toFixed(1).replace('.', ',')
      pushEvidence(rows, 'Похожих связок за последние 30 дней', num(e.observedValue, f), {
        tooltip: BURST_POPULATION_TOOLTIP,
        drilldown: drilldownFor(e.code, 'transition_window_30d', e.observedValue,
          'Похожие связки «импорт → экспорт» за 30 дней до этого кейса',
          'сопоставленные пары «импорт → экспорт» с той же парой кодов груза за 30 календарных дней до дня экспортной отправки — по всем физическим единицам',
          BURST_EXAMPLES_NOTE),
      })
      pushEvidence(rows, 'Похожих связок за последние 90 дней', num(e.baselineValue, f), {
        tooltip: BURST_POPULATION_TOOLTIP,
        drilldown: drilldownFor(e.code, 'transition_window_90d', e.baselineValue,
          'Похожие связки «импорт → экспорт» за 90 дней до этого кейса',
          'сопоставленные пары «импорт → экспорт» с той же парой кодов груза за 90 календарных дней до дня экспортной отправки — по всем физическим единицам',
          BURST_EXAMPLES_NOTE),
      })
      // Вычисляемая величина, а не набор строк: список к ней не прикрепляется.
      pushEvidence(rows, 'Среднее за 30 дней внутри 90-дневного окна', meanText, {
        tooltip: 'Число за 90 дней, делённое на 3. Отдельного набора связок за этим ' +
          'значением нет — оно рассчитано из счётчика за 90 дней.',
      })
      // The service's multiplier is 3*prev_30d/prev_90d — the same ratio as observed/mean.
      // It is shown only when it is above 1 and the two counts differ; equal counts are
      // stated in words instead, because «в 3 раза» would hide that ALL of the 90-day
      // activity fell inside the last 30 days.
      if (e.observedValue !== e.baselineValue && isNum(e.multiplier) && e.multiplier > 1) {
        pushEvidence(rows, 'Превышение среднего уровня', timesPhrase(e.multiplier).replace('в ', ''), {
          tooltip: 'Отношение числа за 30 дней к среднему за 30 дней внутри 90-дневного окна ' +
            '(3 × за 30 дней ÷ за 90 дней). Это коэффициент, а не набор связок.',
        })
      }
      pushEvidence(rows, 'Последние 30 дней', periodValue(e.currentPeriodStart, e.currentPeriodEnd))
      pushEvidence(rows, '90-дневное окно', periodValue(e.baselinePeriodStart, e.baselinePeriodEnd))
      const excess = e.observedValue !== e.baselineValue && isNum(e.multiplier) && e.multiplier > 1
        ? ` Текущая частота выше этого уровня ${timesPhrase(e.multiplier)}.`
        : ''
      return {
        code: e.code,
        title: 'Рост частоты похожих связок',
        text: e.observedValue === e.baselineValue
          ? `За последние 30 дней найдено ${count(e.observedValue, 'похожая связка', 'похожие связки', 'похожих связок', f)} ` +
            '«импорт → экспорт» с той же комбинацией кодов груза — это все связки 90-дневного окна.'
          : `За последние 30 дней найдено ${count(e.observedValue, 'похожая связка', 'похожие связки', 'похожих связок', f)} ` +
            '«импорт → экспорт» с той же комбинацией кодов груза. ' +
            `Средний уровень на один 30-дневный период внутри 90-дневного окна составляет ${meanText}.${excess}`,
        evidence: rows,
        note: '90-дневное окно включает последние 30 дней, поэтому сравнение идёт со средним ' +
          'уровнем внутри него, а не с отдельным предыдущим периодом. Похожими считаются ' +
          'сопоставленные пары «импорт → экспорт» с той же парой кодов груза, по любым вагонам ' +
          'и контейнерам; сам текущий кейс в счёт не входит — окно заканчивается за день до ' +
          'его экспортной отправки.',
        checklistText: `Проверить причину роста частоты: ${count(e.observedValue, 'случай', 'случая', 'случаев', f)} за последние 30 дней при среднем уровне ${meanText}`,
      }
    }

    case 'UNUSUAL_INTERVAL_VS_UNIT_HISTORY': {
      if (!isNum(e.observedValue) || !isNum(e.baselineValue) || !isNum(e.historyCount)) return null
      pushEvidence(rows, 'Интервал этого кейса', count(e.observedValue, 'день', 'дня', 'дней', f))
      pushEvidence(rows, `Обычный интервал ${UNIT_GEN[ctx.unitKind]}`, count(e.baselineValue, 'день', 'дня', 'дней', f), {
        tooltip: 'Среднее по предыдущим сопоставленным парам «импорт → экспорт» этой физической ' +
          'единицы. Набор строк стоит за числом исторических связок ниже, а не за самим средним.',
      })
      pushEvidence(rows, `Исторических связок для ${UNIT_GEN[ctx.unitKind]}`, num(e.historyCount, f), {
        drilldown: drilldownFor(e.code, 'unit_prior_pairs', e.historyCount,
          `Исторические связки «импорт → экспорт» для ${UNIT_GEN[ctx.unitKind]}`, UNIT_PRIOR_SELECTION),
      })
      pushEvidence(rows, 'Период сравнения', period)
      return {
        code: e.code,
        title: 'Необычный интервал между отправками',
        text: `Интервал между отправками отличается от обычного для этого ${unitNom}.`,
        evidence: rows,
        checklistText: `Проверить интервал ${count(e.observedValue, 'день', 'дня', 'дней', f)} при обычном ${count(e.baselineValue, 'дне', 'днях', 'днях', f)}`,
      }
    }

    case 'UNUSUAL_WEIGHT_VS_UNIT_HISTORY': {
      if (!isNum(e.observedValue) || !isNum(e.baselineValue) || !isNum(e.historyCount)) return null
      pushEvidence(rows, 'Разница веса в этом кейсе', `${num(e.observedValue, f)}%`)
      pushEvidence(rows, `Обычная разница веса ${UNIT_GEN[ctx.unitKind]}`, `${num(e.baselineValue, f)}%`, {
        tooltip: 'Среднее по предыдущим сопоставленным парам «импорт → экспорт» этой физической ' +
          'единицы. Набор строк стоит за числом исторических связок ниже, а не за самим средним.',
      })
      pushEvidence(rows, `Исторических связок для ${UNIT_GEN[ctx.unitKind]}`, num(e.historyCount, f), {
        drilldown: drilldownFor(e.code, 'unit_prior_pairs', e.historyCount,
          `Исторические связки «импорт → экспорт» для ${UNIT_GEN[ctx.unitKind]}`, UNIT_PRIOR_SELECTION),
      })
      pushEvidence(rows, 'Период сравнения', period)
      return {
        code: e.code,
        title: 'Необычная разница веса',
        text: `Разница веса между отправками отличается от обычной для этого ${unitNom}.`,
        evidence: rows,
        checklistText: `Сверить разницу веса ${num(e.observedValue, f)}% при обычной ${num(e.baselineValue, f)}%`,
      }
    }

    case 'UNUSUAL_ROUTE_INTERVAL': {
      if (!isNum(e.observedValue) || !isNum(e.baselineValue) || !isNum(e.historyCount)) return null
      pushEvidence(rows, 'Интервал этого кейса', count(e.observedValue, 'день', 'дня', 'дней', f))
      pushEvidence(rows, 'Обычный интервал на этом маршруте', count(e.baselineValue, 'день', 'дня', 'дней', f), {
        tooltip: 'Среднее по сопоставленным парам «импорт → экспорт» этого коридора станций. ' +
          'Набор строк стоит за числом связок по маршруту ниже, а не за самим средним.',
      })
      pushEvidence(rows, 'Связок по маршруту', num(e.historyCount, f), {
        drilldown: drilldownFor(e.code, 'station_corridor_prior_pairs', e.historyCount,
          'Связки «импорт → экспорт» по этому коридору станций',
          'сопоставленные пары «импорт → экспорт» по этому коридору станций до текущего кейса, от новых к старым'),
      })
      pushEvidence(rows, 'Период сравнения', period)
      return {
        code: e.code,
        title: 'Необычный интервал для этого маршрута',
        text: 'Интервал между отправками отличается от обычного для этого маршрута.',
        evidence: rows,
        checklistText: `Проверить интервал ${count(e.observedValue, 'день', 'дня', 'дней', f)} при обычном для маршрута ${count(e.baselineValue, 'дне', 'днях', 'днях', f)}`,
      }
    }

    default:
      return null
  }
}

export interface CaseExplanation {
  /** One-line conclusion above the cards, or null when none can be stated. */
  summary: string | null
  reasons: RenderedReason[]
  /** True when nothing could be stated with numbers — show NO_EVIDENCE_TEXT. */
  isEmpty: boolean
  /** Some model reasons had no verified numbers and were left out. */
  hasWithheld: boolean
  /** Почему раздел пуст. `null`, когда основания показаны. */
  emptyCause: NoEvidenceCause | null
  /** Готовый текст под это состояние. `null`, когда основания показаны. */
  emptyText: string | null
}

/**
 * Section conclusion, assembled from whichever supported reasons are present.
 * Route/direction is deliberately absent: it is not part of the evidence behind
 * these reasons, so stating it here would be an unbacked claim.
 */
export function buildSectionSummary(
  reasons: RenderedReason[],
  ctx: ExplanationContext = DEFAULT_CTX,
): string | null {
  const codes = new Set(reasons.map((r) => r.code))
  const global = codes.has('UNSEEN_GLOBAL_CARGO_TRANSITION')
  const unit = codes.has('NEW_UNIT_TRANSITION')
  const unitRef = ctx.unitNumber
    ? `${UNIT_GEN[ctx.unitKind]} ${ctx.unitNumber}`
    : `этого ${UNIT_GEN[ctx.unitKind]}`

  if (global && unit) {
    return 'Текущая комбинация кодов груза «ввоз → вывоз» впервые встречается в доступных ' +
      `данных и впервые зафиксирована для ${unitRef}.`
  }
  if (global) {
    return 'Текущая комбинация кодов груза «ввоз → вывоз» впервые встречается в доступных ' +
      'данных за указанный период.'
  }
  if (unit) {
    return `Для ${unitRef} текущая комбинация кодов груза «ввоз → вывоз» зафиксирована впервые.`
  }
  if (reasons.length > 0) {
    return 'Кейс отличается от исторических связок «импорт → экспорт» по признакам ниже.'
  }
  return null
}

/** Strongest 1–3 supported reasons, plus the section conclusion. Never pads to three. */
export function buildCaseExplanation(
  f: Formatter,
  explanation: AnomalyExplanation | undefined,
  ctx: ExplanationContext = DEFAULT_CTX,
  max = 3,
): CaseExplanation {
  const evidences = explanation?.reasons ?? []

  // Показ, а не аналитика. Полный аналитический набор кодов кейса — это
  // поддержанные числами коды ПЛЮС те, под которые чисел не нашлось; решение о
  // видимости принимается по нему целиком, потому что продуктовое правило
  // сформулировано на `reasonCodes`, а не на подмножестве с доказательствами.
  // Ни `explanation`, ни `explanation.reasons` при этом не изменяются.
  const analyticalCodes = [
    ...evidences.map((e) => e.code),
    ...(explanation?.unsupportedReasonCodes ?? []),
  ]
  const visibleCodes = new Set(getVisibleAnomalyReasons(analyticalCodes))

  const rendered: RenderedReason[] = []
  for (const e of evidences) {
    // Скрытая причина не доходит до карточки — а значит и её таблица
    // доказательств не остаётся висеть сама по себе: они подавляются вместе.
    // Сами данные доказательств в `explanation` остаются нетронутыми.
    if (!visibleCodes.has(e.code)) continue
    const r = renderReason(f, e, ctx)
    if (r) rendered.push(r)
  }
  rendered.sort((a, b) => (REASON_PRIORITY[b.code] ?? 0) - (REASON_PRIORITY[a.code] ?? 0))
  const shown = rendered.slice(0, max)

  // The cargo code is context, not a finding: keep it on the first table that carries it
  // and drop the repeats, so it is not restated under every reason.
  let cargoShown = false
  for (const r of shown) {
    r.evidence = r.evidence.filter((row) => {
      if (row.label !== 'Код груза') return true
      if (cargoShown) return false
      cargoShown = true
      return true
    })
    // Fewer than two supported numbers is not a table — and not a licence to fall back on
    // a generic sentence either.
    if (r.evidence.length < MIN_EVIDENCE_ROWS) {
      r.evidence = []
      r.text = INSUFFICIENT_EVIDENCE_TEXT
      r.note = null
      // No table, no checklist line: an item that points at numbers the card does not show
      // would send the analyst looking for something that is not there.
      r.checklistText = null
    }
  }

  return {
    summary: buildSectionSummary(shown, ctx),
    reasons: shown,
    isEmpty: shown.length === 0,
    emptyCause: shown.length === 0 ? noEvidenceCause(explanation, ctx.anomalyCohort) : null,
    emptyText: shown.length === 0
      ? noEvidenceText(noEvidenceCause(explanation, ctx.anomalyCohort), ctx)
      : null,
    hasWithheld: (explanation?.unsupportedReasonCodes?.length ?? 0) > 0 || rendered.length > shown.length,
  }
}

/** Checklist lines carrying the same numbers as the reason tables, in the API's order. */
export function buildEvidenceChecklist(reasons: RenderedReason[]): string[] {
  return reasons.map((r) => r.checklistText).filter((t): t is string => !!t)
}

/** «Насколько необычен: 9.86 из 10» — plain wording, no model vocabulary. */
export function scoreOutOfTen(v: number | null): string {
  return typeof v === 'number' && Number.isFinite(v) ? `${v.toFixed(2)} из 10` : '—'
}

/** Top-of-drawer summary. Deliberately says nothing a score cannot support. */
export function buildTopSummary(row: AnomalyCaseRow, exp: CaseExplanation): string {
  if (exp.isEmpty) {
    // Прежняя фраза «числовые основания ниже не приводятся» верна, но молчит о
    // причине; она называется здесь же, чтобы раздел ниже не выглядел сбоем.
    const why = exp.emptyCause === 'no_history_baseline'
      ? 'Недостаточно предыдущей истории для детального сравнения.'
      : exp.emptyCause === 'reasons_without_numbers'
        ? 'Числовой детализации для выделенной причины пока нет.'
        : 'Отдельную доминирующую причину модель не выделила.'
    return `Кейс отобран моделью как отличающийся от похожих исторических связок. ${why}`
  }
  return 'Кейс сильно отличается от похожих исторических связок. Основные отличия показаны ниже.'
}
